import { useState, type FC } from 'react';
import { IconButton, OverflowMenu, OverflowMenuItem } from '@carbon/react';
import { Close } from '@carbon/icons-react';

import { SOBRE_A_CONVERSA } from '../../logica/nyta/explicacoes';
import { ConfirmarPerigo } from '../padroes/ConfirmarPerigo';
import { RotuloDeIA } from '../padroes/ia/RotuloDeIA';
import { Conversa } from '../telas/nyta/Conversa';
import { NytaTravada } from '../telas/nyta/PaginaDaNyta';
import styles from './NytaFlutuante.module.scss';

// O PAINEL DA NYTA, sem o hook: fica num arquivo à parte para a landing desenhá-lo sem carregar a
// conversa de verdade (sessão, histórico, servidor). Quem liga o painel à conversa é `NytaFlutuante`.

/**
 * O painel, sem lógica: a vitrine o desenha com dados de exemplo, e a landing (`emLinha`) com a
 * conversa de demonstração. Em linha ele não flutua nem é diálogo: fica no fluxo da página, e não
 * tem o fechar, que não teria o que fechar.
 */
export const PainelDaNyta: FC<{
  c: Parameters<typeof Conversa>[0]['c'];
  focoNoTitulo?: React.Ref<HTMLHeadingElement>;
  telaCheia?: boolean;
  emLinha?: boolean;
}> = ({ c, focoNoTitulo, telaCheia, emLinha }) => {
  const [limpando, setLimpando] = useState(false);
  const idDoTitulo = emLinha ? 'nyta-demonstracao-titulo' : 'nyta-flutuante-titulo';
  return (
    // No celular o painel cobre a tela: aí ele é modal (e a página atrás fica inerte).
    <section className={`${styles.painel} ${emLinha ? styles.emLinha : ''}`} role={emLinha ? 'region' : 'dialog'}
      aria-modal={emLinha ? undefined : telaCheia ? 'true' : 'false'} aria-labelledby={idDoTitulo}>
      <header className={styles.topo}>
        {/* O "IA" é a marca da Nyta e o rótulo amplo (a conversa inteira é IA, e ele explica). */}
        <RotuloDeIA sobre={SOBRE_A_CONVERSA} tamanho='xs' alinhar='bottom-start' />
        <span className={styles.quem}>
          <h2 id={idDoTitulo} className={styles.nome} tabIndex={-1} ref={focoNoTitulo}>Nyta</h2>
        </span>
        <span className={styles.acoes}>
          {!c.travada && (
            <OverflowMenu size='sm' flipped aria-label='Mais opções da Nyta' iconDescription='Mais opções da Nyta'>
              <OverflowMenuItem itemText='Nova conversa' onClick={c.novaConversa} />
              {c.telaCheia && <OverflowMenuItem itemText='Abrir em tela cheia' onClick={c.telaCheia} />}
              {c.limpar && <OverflowMenuItem itemText='Limpar esta conversa' isDelete hasDivider onClick={() => setLimpando(true)} />}
            </OverflowMenu>
          )}
          {!emLinha && <IconButton kind='ghost' size='sm' label='Fechar a Nyta' onClick={c.painel.fechar}><Close /></IconButton>}
        </span>
      </header>
      <div className={styles.corpo}>
        {c.travada ? <div className={styles.travada}><NytaTravada t={c.travada} /></div> : <Conversa c={c} compacta />}
      </div>
      <ConfirmarPerigo
        aberto={limpando}
        rotulo='Nyta'
        titulo='Limpar esta conversa?'
        texto='As mensagens desta conversa serão apagadas. Não dá para desfazer.'
        acao='Limpar'
        manter='Manter mensagens'
        aoConfirmar={() => { c.limpar?.(); setLimpando(false); }}
        aoFechar={() => setLimpando(false)}
      />
    </section>
  );
};
